import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { client } from '../amplify-client';
import { GuennieRating } from '../components/GuennieRating';
import { SpaghettiDivider } from '../components/SpaghettiDivider';
import { formatDatum } from '../lib/format';
import type { Schema } from '../../amplify/data/resource';

type Test = Schema['SpaghettiEisTest']['type'];
type EisdieleKurz = { id: string; name: string; ort: string };

type Sortierung = 'neueste' | 'guennies';

export function Verkostungen() {
  const [tests, setTests] = useState<Test[]>([]);
  const [eisdielen, setEisdielen] = useState<Record<string, EisdieleKurz>>({});
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const [ortFilter, setOrtFilter] = useState('');
  const [eisdieleFilter, setEisdieleFilter] = useState('');
  const [minGuennies, setMinGuennies] = useState(0);
  const [jahrFilter, setJahrFilter] = useState('');
  const [testerFilter, setTesterFilter] = useState('');
  const [sortierung, setSortierung] = useState<Sortierung>('neueste');

  useEffect(() => {
    Promise.all([
      client.queries.listeVeroeffentlichteTests({ limit: 500 }),
      client.models.Eisdiele.list({ selectionSet: ['id', 'name', 'ort'], limit: 2000 }),
    ])
      .then(([testRes, eisdieleRes]) => {
        const gefundeneTests = (testRes.data ?? []).filter(Boolean) as Test[];
        setTests(gefundeneTests);
        const map: Record<string, EisdieleKurz> = {};
        for (const e of eisdieleRes.data ?? []) map[e.id] = { id: e.id, name: e.name, ort: e.ort };
        setEisdielen(map);
      })
      .catch((e) => setFehler(String(e?.message ?? e)))
      .finally(() => setLaedt(false));
  }, []);

  const orte = useMemo(
    () => Array.from(new Set(Object.values(eisdielen).map((e) => e.ort))).sort((a, b) => a.localeCompare(b, 'de')),
    [eisdielen],
  );
  const jahre = useMemo(
    () => Array.from(new Set(tests.map((t) => t.besuchsdatum?.slice(0, 4)).filter(Boolean))).sort().reverse(),
    [tests],
  );
  const testerNamen = useMemo(
    () => Array.from(new Set(tests.map((t) => t.testerName))).sort((a, b) => a.localeCompare(b, 'de')),
    [tests],
  );

  const gefiltert = useMemo(() => {
    let liste = tests.filter((t) => {
      const eisdiele = eisdielen[t.eisdieleId];
      if (ortFilter && eisdiele?.ort !== ortFilter) return false;
      if (eisdieleFilter && t.eisdieleId !== eisdieleFilter) return false;
      if (minGuennies > 0 && (t.gesamtGuennies ?? 0) < minGuennies) return false;
      if (jahrFilter && !t.besuchsdatum?.startsWith(jahrFilter)) return false;
      if (testerFilter && t.testerName !== testerFilter) return false;
      return true;
    });
    liste = [...liste].sort((a, b) =>
      sortierung === 'guennies'
        ? (b.gesamtGuennies ?? 0) - (a.gesamtGuennies ?? 0)
        : (b.besuchsdatum ?? '').localeCompare(a.besuchsdatum ?? ''),
    );
    return liste;
  }, [tests, eisdielen, ortFilter, eisdieleFilter, minGuennies, jahrFilter, testerFilter, sortierung]);

  return (
    <div className="container">
      <p className="label">Aus der Presse</p>
      <h1>Alle Verkostungen</h1>
      <SpaghettiDivider />

      <div
        className="karte"
        style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginBottom: '1.5rem',
        }}
      >
        <div>
          <label htmlFor="f-ort">Ort</label>
          <select id="f-ort" value={ortFilter} onChange={(e) => setOrtFilter(e.target.value)}>
            <option value="">alle</option>
            {orte.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="f-eisdiele">Eisdiele</label>
          <select id="f-eisdiele" value={eisdieleFilter} onChange={(e) => setEisdieleFilter(e.target.value)}>
            <option value="">alle</option>
            {Object.values(eisdielen)
              .sort((a, b) => a.name.localeCompare(b.name, 'de'))
              .map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
          </select>
        </div>
        <div>
          <label htmlFor="f-guennies">Mind. Günnies</label>
          <select id="f-guennies" value={minGuennies} onChange={(e) => setMinGuennies(Number(e.target.value))}>
            <option value={0}>alle</option>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                ≥ {n}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="f-jahr">Jahr</label>
          <select id="f-jahr" value={jahrFilter} onChange={(e) => setJahrFilter(e.target.value)}>
            <option value="">alle</option>
            {jahre.map((j) => (
              <option key={j} value={j}>
                {j}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="f-tester">Tester</label>
          <select id="f-tester" value={testerFilter} onChange={(e) => setTesterFilter(e.target.value)}>
            <option value="">alle</option>
            {testerNamen.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <label htmlFor="f-sort">Sortieren nach</label>
          <select id="f-sort" value={sortierung} onChange={(e) => setSortierung(e.target.value as Sortierung)}>
            <option value="neueste">neueste zuerst</option>
            <option value="guennies">meiste Günnies</option>
          </select>
        </div>
      </div>

      {laedt ? <p className="label">Lädt …</p> : null}
      {fehler ? <p className="hinweis">Konnte die Liste nicht laden: {fehler}</p> : null}
      {!laedt && gefiltert.length === 0 ? (
        <p style={{ color: 'var(--espresso-70)' }}>Keine Verkostungen gefunden.</p>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.75rem' }}>
        {gefiltert.map((t) => {
          const eisdiele = eisdielen[t.eisdieleId];
          return (
            <Link
              key={t.id}
              to={`/app/verkostungen/${t.id}`}
              className="karte"
              style={{
                textDecoration: 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <strong style={{ fontFamily: 'var(--font-display)' }}>
                  {eisdiele?.name ?? 'Unbekannte Eisdiele'}
                </strong>
                <div style={{ color: 'var(--espresso-70)', fontSize: '0.9rem' }}>
                  {eisdiele?.ort ?? ''} · {formatDatum(t.besuchsdatum)} · {t.testerName}
                </div>
              </div>
              <GuennieRating wert={t.gesamtGuennies} groesse="sm" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
